// "Schedule release" — plan a release that hasn't shipped yet. Creates a
// draft release with no GitHub builds: a declared surface, a target ship
// date, and (mobile) an optional build-cut date. Both dates mirror straight
// to the shared release calendar; when the real GitHub release lands the
// editor attaches it via +Add build on the release page.
import { useState } from "react";
import { useLocation } from "wouter";
import { useCreatePlannedRelease } from "@/api/mutations";
import { SideDrawer } from "@/components/SideDrawer";
import { Button } from "@/ui/button";
import { cn } from "@/lib/cn";

/** Next Monday strictly after today — the default mobile release slot. */
function nextMonday(): string {
  const d = new Date();
  do {
    d.setDate(d.getDate() + 1);
  } while (d.getDay() !== 1);
  return toIsoDay(d);
}

/** The Wednesday strictly before the given day — the default build cut.
 *  Mirrors the server's rule so the pre-filled value matches what omitting
 *  the field would produce. */
function wednesdayBefore(isoDay: string): string {
  const [y, m, day] = isoDay.split("-").map(Number);
  const d = new Date(y!, m! - 1, day!);
  do {
    d.setDate(d.getDate() - 1);
  } while (d.getDay() !== 3);
  return toIsoDay(d);
}

function toIsoDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const inputCls =
  "w-full rounded-md border border-border-hover bg-bg px-3 py-2 text-sm text-text outline-none focus:border-text/50";

export function ScheduleReleaseDrawer() {
  return (
    <SideDrawer
      width="max-w-md"
      title="Schedule release"
      description="Plan an upcoming release before anything ships. It lands on the release calendar right away; attach the real GitHub build later from the release page."
      trigger={
        <button
          type="button"
          className="rounded-md border border-border-hover bg-card px-3 py-1.5 text-sm text-text-secondary hover:border-text/40 hover:text-text"
        >
          + Schedule release
        </button>
      }
    >
      <ScheduleReleaseBody />
    </SideDrawer>
  );
}

function ScheduleReleaseBody() {
  const [, navigate] = useLocation();
  const create = useCreatePlannedRelease();

  const [surface, setSurface] = useState<"mobile" | "web">("mobile");
  const [title, setTitle] = useState("");
  const [targetDate, setTargetDate] = useState(nextMonday);
  // Build cut follows the target ("Wednesday before") until the editor
  // touches it — then their explicit choice sticks across target changes.
  const [cutTouched, setCutTouched] = useState(false);
  const [cutEnabled, setCutEnabled] = useState(true);
  const [cutDate, setCutDate] = useState(() => wednesdayBefore(nextMonday()));
  const [error, setError] = useState("");

  const effectiveCut = cutTouched ? cutDate : wednesdayBefore(targetDate);
  const showCut = surface === "mobile";
  const busy = create.isPending;

  const submit = async () => {
    setError("");
    if (!title.trim()) {
      setError("Give the release a version or name (e.g. 4.13.0).");
      return;
    }
    if (!targetDate) {
      setError("Pick a target date.");
      return;
    }
    try {
      const created = await create.mutateAsync({
        title: title.trim(),
        surface,
        target_date: targetDate,
        build_cut_date: showCut && cutEnabled ? effectiveCut : null,
      });
      navigate(`/releases/${created.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-text">Platform</label>
        <div className="inline-flex rounded-md border border-border-hover bg-bg p-0.5">
          {(
            [
              ["mobile", "Mobile"],
              ["web", "Web"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setSurface(v)}
              className={cn(
                "rounded-sm px-3 py-1 text-xs",
                surface === v
                  ? "bg-card font-medium text-text shadow-sm"
                  : "text-text-secondary hover:text-text",
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-text" htmlFor="sched-title">
          Version / title
        </label>
        <input
          id="sched-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={surface === "mobile" ? "e.g. 4.13.0" : "e.g. Portal weekly"}
          className={inputCls}
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-text" htmlFor="sched-target">
          Target date
        </label>
        <input
          id="sched-target"
          type="date"
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
          className={cn(inputCls, "max-w-[200px]")}
        />
        <p className="text-xs text-text-tertiary">
          Mobile releases usually ship on Mondays — the default is the next one.
        </p>
      </div>

      {showCut && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-text">
            <input
              type="checkbox"
              checked={cutEnabled}
              onChange={(e) => setCutEnabled(e.target.checked)}
            />
            Build cut
          </label>
          {cutEnabled && (
            <>
              <input
                type="date"
                value={effectiveCut}
                onChange={(e) => {
                  setCutTouched(true);
                  setCutDate(e.target.value);
                }}
                className={cn(inputCls, "max-w-[200px]")}
                aria-label="Build cut date"
              />
              <p className="text-xs text-text-tertiary">
                Defaults to the Wednesday before the target. Adds a{" "}
                <span className="whitespace-nowrap">“Build Cut EOD”</span> event
                to the release calendar.
              </p>
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 pt-1">
        <Button onClick={submit} loading={busy} disabled={busy}>
          {busy ? "Scheduling…" : "Schedule release"}
        </Button>
        {error && <span className="text-sm text-destructive">{error}</span>}
      </div>
    </div>
  );
}
